<template>
    <section class="booking" v-if="dictionary != null">
        <div class="booking__main">

            <div class="booking-panel" v-if="showRoomBrowser === null">
                <p class="booking__headline">{{ dictionary.BookingFlowRoomsExistingRoomHeadline }}</p>
                <p>
                    {{ dictionary.BookingFlowRoomsExistingRoomText }}
                </p>
                <br />
                <div class="booking-button-list">

                    <Button class="p-button-primary p-button-rounded" @click="() => { showRoomBrowser = false; }"
                        label="Ja"></Button>

                    <Button class="p-button-primary p-button-rounded" @click="() => { showRoomBrowser = true; }"
                        label="Nej"></Button>
                </div>
            </div>

            <div class="booking-panel" v-if="showRoomBrowser === false">
                <p class="booking__headline">{{ dictionary.BookingFlowRoomsExistingRoomHeadline }}</p>
                <div class="booking__existing-room booking__input-group">
                    <small v-if="manualRoomNumber != null && !manualRoomNumberValid" class="p-error">{{
                        dictionary.BookingFlowRoomsExistingRoomInputValidationError
                        }}</small>
                    <InputText type="number" v-model="manualRoomNumber"
                        :class="{ 'p-invalid': manualRoomNumber != null && !manualRoomNumberValid }"
                        :placeholder="dictionary.BookingFlowRoomsExistingRoomInputPlaceholder" />
                    <Button class="p-button-primary p-button-rounded" :disabled="!manualRoomNumberValid"
                        @click="handleAddRoom()" :label="dictionary.BookingFlowRoomsAddButtonText"></Button>

                    <button type="button" class="booking__text-button" @click="() => { showRoomBrowser = true; }">{{
                        dictionary.BookingFlowRoomsSwitchToRoomBrowser }}</button>
                </div>


            </div>
            <div class="booking-panel" v-if="!hasRooms">
                <p class="booking__headline" style="text-align:center">{{ dictionary.BookingFlowRoomsNoRoomsHeadline }}
                </p>
                <BookingAlternatives :departmentId="department.Id" :waitingListLink="waitingListLink"
                    supportLink="#generalContact" :findOtherLink="formattedFindOtherLinks" :expanded="true" />
            </div>
            <div class="booking-panel" v-if="hasRooms && showRoomBrowser === true">
                <div class="room-list__header">
                    <p class="booking__headline">{{ dictionary.BookingFlowRoomsListHeadline }}</p>
                    <SelectButton v-model="selectedRoomUnit" :options="roomUnits" :unselectable="false"
                        optionLabel="name">
                        <template #option="slotProps">
                            <span v-html="slotProps.option.name"></span>
                        </template>
                    </SelectButton>
                </div>
                <RoomSizeSelector :initialMin="initialSize[0]" :initialMax="initialSize[1]" :minimum="department.MinSqm"
                    :maximum="department.MaxSqm" :unit="selectedRoomUnit.value" :availability="!showAll.value"
                    :dictionary="dictionary" @sliderChanged="handleRangeSlideChange" />

                <!-- NO AVAILABLE ROOMS BEGIN -->
                <div class="room-list__no-available-message" v-if="showNoAvailableMessage">
                    <img src="/assets/images/error-circle.svg" width="22" hight="22" alt="" role="presentation"
                        loading="lazy" />
                    <p>{{ dictionary.BookingFlowRoomsNoAvailableMessage }}</p>
                </div>

                <BookingAlternatives v-if="!showNoAvailableMessage" :departmentId="department.Id"
                    :waitingListLink="waitingListLink" supportLink="#generalContact"
                    :findOtherLink="formattedFindOtherLinks" />

                <div class="booking__room-combinations" v-if="combinations && showNoAvailableMessage">
                    <p class="booking__headline">
                        {{ dictionary.BookingFlowRoomsCombinationsHeadline }}</p>
                    <div class="room-list">
                        <div v-for="(combination, index) in combinations" :key="index" class="room-list-item">
                            <img src="/assets/images/greencheck.svg" width="24" height="24" alt="" role="presentation"
                                loading="lazy" />
                            <div class="room-list-item__info">
                                <div class="room-list-item__size">
                                    <img src="/assets/images/greencheck.svg" width="24" height="24" alt=""
                                        role="presentation" loading="lazy" />
                                    <p>Kombination: <br />
                                        <span v-html="getSizeString(combination.Item1)"></span> + <span
                                            v-html="getSizeString(combination.Item2)"></span>
                                    </p>
                                </div>
                                <div class="room-list-item__price-wrap">
                                    <div class="room-list-item__price">
                                        {{
                                            combination.Item1.DefaultPriceText
                                        }}
                                    </div>
                                    <div v-if="combination.Item1.CampaignPriceText" class="room-list-item__campaign">
                                        {{
                                            combination.Item1.CampaignPriceText
                                        }}
                                    </div>
                                </div>
                            </div>
                            <div class="room-list-item__actions">
                                <Button class="p-button-primary p-button-rounded"
                                    :label="dictionary.BookingFlowRoomsAddCombinationButtonText"
                                    @click="() => { addRoom(combination.Item1, true); addRoom(combination.Item2, true); }"></Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="booking__available-departments"
                    v-show="availableDepartments.length > 0 && showNoAvailableMessage">
                    <p class="booking__sub-headline">{{ dictionary.BookingFlowRoomsAvailableDepartmentsHeadline }}</p>
                    <div class="booking__available-departments-list-wrap
                    swiper" id="nearbyDepartmentsSlider" ref="nearbyDepartmentsSlider">
                        <div class="booking__available-departments-list swiper-wrapper">
                            <DepartmentCard v-for="(department, index) in availableDepartments" :key="index"
                                :department="department" :noSelect="true"
                                :urlAddition="`?${queryKeys.fromRecommendation}`">
                            </DepartmentCard>
                        </div>
                        <div class="booking__available-departments-navigation" v-show="availableDepartments.length > 1">
                            <div class="booking__available-departments-prev"><img src="/assets/images/arrow-left.svg"
                                    width="42" height="42" alt="" role="presentation" loading="lazy" /></div>
                            <div class="booking__available-departments-next"><img src="/assets/images/arrow-right.svg"
                                    width="42" height="42" alt="" role="presentation" loading="lazy" /></div>
                        </div>
                    </div>
                </div>

                <!-- NO AVAILABLE ROOMS END -->

                <!-- ROOMS LIST BEGIN -->
                <div v-if="!showNoAvailableMessage" class="room-list__filter">
                    <SelectButton v-model="showAll" :options="roomListOptions" :unselectable="false" optionLabel="name"
                        :disabled="disableAvailableButton">
                        <template #option="slotProps">
                            <span v-html="slotProps.option.name"></span>
                        </template>
                    </SelectButton>
                </div>
                <p v-if="showNoAvailableMessage" class="booking__sub-headline">{{
                    dictionary.BookingFlowRoomsWaitlistHeadline }}</p>
                <DataView class="room-list" v-if="filteredRooms != null && filteredRooms.length > 0"
                    :value="filteredRooms" :paginator="true" :rows="11" :totalRecords="filteredRooms.length"
                    :first="((page - 1) * 11)" @page="(e) => { page = parseInt(e.page) + 1; }">
                    <template #list="slotProps">
                        <div class="room-list-item"
                            :class="{ 'room-list-item--active': basket.some(x => x.room.Size == slotProps.data[0].Size), 'room-list-item--disabled': slotProps.data[0].Available == 0 }">
                            <div class="room-list-item__info">
                                <div class=room-list-item__size>
                                    <p>{{ dictionary.BookingFlowCommonSize }}: <span
                                            v-html="getSizeString(slotProps.data[0])"></span></p>
                                    <small>{{
                                        dictionary.BookingFlowRoomsEmptyRoomCount.replace('{count}',
                                            slotProps.data[0].Available != null ? slotProps.data[0].Available :
                                                slotProps.data.length)
                                    }}</small>
                                </div>
                                <div class="room-list-item__price-wrap">
                                    <div class="room-list-item__price">
                                          {{ slotProps.data[0].DefaultPriceText }}
                                    </div>
                                    <div v-if="slotProps.data[0].CampaignPriceText"
                                        class="room-list-item__campaign">
                                        {{slotProps.data[0].CampaignPriceText }}
                                    </div>
                                </div>
                            </div>
                            <div class="room-list-item__actions">
                                <Button class="p-button-primary p-button-rounded"
                                    v-if="slotProps.data[0].Available != 0"
                                    :label="dictionary.BookingFlowRoomsAddButtonText"
                                    @click="addRoom(slotProps.data)"></Button>
                                <Button class="p-button-secondary p-button-rounded p-button-outlined"
                                    v-if="slotProps.data[0].Available == 0"
                                    @click="handleWaitingListClick(slotProps.data[0].Size)">Venteliste</Button>
                            </div>
                        </div>
                    </template>
                </DataView>
                <!-- ROOMS LIST END -->
                <div class="booking__show-manual-entry-wrap">
                    <button type="button" class="booking__text-button booking__show-manual-entry"
                        @click="handleShowManualEntry">{{
                            dictionary.BookingFlowRoomsSwitchToManualEntry
                        }}</button>
                </div>
            </div>
        </div>

        <aside class="booking__aside">
            <div class="booking__basket booking-panel">
                <BookingBasket :department="department" :basket="basket" :basketSummary="basketSummary" :padlockPrice="padlockPrice"
                    :invitationCode="invitationCode" :nextStepButtonText="'Næste'" :nextStep="nextStep" :step="step"
                    :steps="steps" 
                    v-bind="$attrs" />
                <BookingProgress :progress="progress"></BookingProgress>
            </div>
            <div class="booking-panel booking-panel--solar-powered" v-if="department.SolarPowered">
                <SolarPowered></SolarPowered>
            </div>
            <div class="booking-panel">
                <Help></Help>
            </div>
        </aside>
        <Sidebar v-model:visible="showBasketOverlay" position="bottom">
            <BookingBasket :department="department" :basket="basket"  :basketSummary="basketSummary" :padlockPrice="padlockPrice"
                :invitationCode="invitationCode" :nextStepButtonText="'Næste'" :nextStep="nextStep" :step="step"
                :steps="steps" 
                v-bind="$attrs" />
        </Sidebar>
        <div class="booking-mobile-bar">
            <nav class="booking-mobile-bar__navigation">
                <Button class="p-button-text p-button-rounded p-button-sm booking-mobile-bar__basket-button"
                    @click="showBasketOverlay = true" icon="pi pi-list"
                    :label="`Se valgte rum (${basket.length})`"></Button>
                <Button :disabled="basketIsEmpty" class="p-button-primary p-button-rounded p-button-sm"
                    @click="handleOutsideBasketStepChange">Næste</Button>
            </nav>
            <BookingProgress :progress="progress"></BookingProgress>
        </div>
    </section>
</template>
<script>
import Swiper from 'swiper';
import { Navigation } from "swiper/modules";
Swiper.use([Navigation]);

import bfdl from './../../bookingFlowDataLayer.js';
import RoomSizeSelector from "../components/roomSizeSelector.vue";
import BookingBasket from "../components/bookingBasket.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from 'primevue/dropdown';
import PadlockPrompt from '../components/padlockPrompt.vue';
import DataView from 'primevue/dataview';
import OverlayPanel from 'primevue/overlaypanel';
import Toast from 'primevue/toast';
import Sidebar from 'primevue/sidebar';
import Help from './../components/help.vue';
import SolarPowered from './../components/solarPowered.vue';
import BookingProgress from '../components/bookingProgress.vue';
import SelectButton from 'primevue/selectbutton';
import BookingAlternatives from '../components/bookingAlternatives.vue';
import DepartmentCard from '../components/departmentCard.vue';

export default {
    name: "rooms",
    components: {
        RoomSizeSelector,
        BookingBasket,
        Button,
        InputText,
        InputNumber,
        Dropdown,
        PadlockPrompt,
        DataView,
        OverlayPanel,
        Toast,
        Help,
        SolarPowered,
        Sidebar,
        BookingProgress,
        SelectButton,
        BookingAlternatives,
        DepartmentCard
    },
    props: ["department", "rooms", "progress", "basket","basketSummary", "padlockPrice", "hasRooms", "parentLink", "nearestDepartments", "waitingListLink", "supportLink", "findOtherLink", "invitationCode", "step", "steps", "nextStep", "queryKeys", "hasSeenNoAvailableRoomsMessage"],
    data() {
        return {
            dictionary: null,
            selectedRoomUnit: null,
            roomUnits: [
                { name: 'm<sup>2</sup>', value: 'area' },
                { name: 'm<sup>3</sup>', value: 'volume' }
            ],
            roomListOptions: [
                { name: 'Alle rum', value: true },
                { name: 'Ledige rum', value: false }
            ],
            combinations: null,
            nearbyDepartmentsSlider: null,
            availableDepartments: [],
            filteredRooms: null,
            showRoomBrowser: null,
            manualRoomNumber: null,
            manualRoomNumberValid: true,
            showBasketOverlay: false,
            showAll: false,
            disableAvailableButton: false,
            selectedSize: null,
            initialFetch: true,
            showNoAvailableMessage: false,
            showNearbyDepartment: false,
            roomFetchRequestCount: 0,
            initialSize: null,
            page: 1
        };
    },
    created() {
        const vm = this;
        vm.selectedRoomUnit = vm.roomUnits[0];
        vm.showAll = vm.roomListOptions[1];

        var searchParams = new URLSearchParams(window.location.search);
        const minSizeFromQuery = searchParams.get(vm.queryKeys.minSize);
        const maxSizeFromQuery = searchParams.get(vm.queryKeys.maxSize);
        if (minSizeFromQuery && maxSizeFromQuery) {
            let maxSizeFromQuerParsed = parseInt(maxSizeFromQuery);
            if (maxSizeFromQuerParsed > 10) {
                maxSizeFromQuerParsed = 10;
            }
            const size = [parseInt(minSizeFromQuery), maxSizeFromQuerParsed];
            vm.initialSize = size;
            vm.selectedSize = size;
            vm.showRoomBrowser = true;
        } else {
            const availableRooms = vm.rooms.filter(x => x.some(y => y.Available == null)).flat();

            if (availableRooms.length) {
                let minSize = Math.floor(availableRooms[0].SizeNumeric)
                let maxSize = Math.floor(availableRooms[availableRooms.length - 1].SizeNumeric);
                if (maxSize > 10) {
                    maxSize = 10;
                }
                vm.initialSize = [minSize, maxSize];
                vm.selectedSize = [minSize, maxSize];
            }

            var isMobile = window.matchMedia("(max-width:665px)");
            if (isMobile.matches) {
                this.showRoomBrowser = false;

            } else {
                this.showRoomBrowser = true;
            }
        }

        fetch("/umbraco/api/dictionaryapi/GetRoomsDictionary").then((response) => response.json())
            .then((data) => {
                vm.dictionary = data;
            })
            .catch((error) => {
                console.log(error);
            });
    },
    computed: {
        hasFilteredRooms() {
            return this.filteredRooms != null && this.filteredRooms.length > 0;
        },
        basketIsEmpty() {
            return this.basket.length == 0;
        },
        formattedFindOtherLinks() {
            return `${this.findOtherLink}?lat=${this.department.Coordinates.Latitude}&lng=${this.department.Coordinates.Longitude}&city=${this.department.City}`;
        }
    },
    methods: {
        getRooms(min, max, showOnlyAvailable = false) {
            const vm = this;
            min = parseInt(min);
            max = parseInt(max);
            vm.filteredRooms = vm.rooms.filter(x => x.some(y => y.SizeNumeric >= min && (max < 10 ? y.SizeNumeric <= max : true) && (showOnlyAvailable ? y.Available == null : true))); //Right now, number of available rooms is not implemented, so its null if it's available, and 0 if none is available
            //If max is more than 10, dont set a max, just go all the way to the largest rooms
        },
        handleShowManualEntry() {
            this.showRoomBrowser = false;

            this.$nextTick(() => {
                const manualEntryField = this.$el.querySelector('.booking__existing-room');
                if (manualEntryField) {
                    window.scrollTo({ top: manualEntryField.offsetTop, left: 0, behavior: 'smooth' });
                }
            });
        },
        handleOutsideBasketStepChange() {
            this.$emit("stepChange", this.nextStep);
        },
        addRoom(rooms, checkExistence = false) {
            this.$emit("addRoom", rooms, checkExistence);
        },
        removeRoom(roomSize) {
            this.$emit("removeRoom", roomSize);
        },
        handleWaitingListClick(size) {
            window.location.href = this.waitingListLink + "?departmentId=" + this.department.Id + "&size=" + size;
        },
        getSizeString(room) {
            switch (this.selectedRoomUnit.value) {
                case "volume":
                    return `${room.Volume} m<sup>3<sup>`
                case "area":
                default:
                    return `${room.Size} m<sup>2<sup>`
            }
        },
        async handleRangeSlideChange(size) {
            const vm = this;
            vm.$emit('toggleSpinner', true);
            vm.combinations = null;
            vm.availableDepartments = [];
            vm.selectedSize = size;
            vm.getRooms(size[0], size[1]);

            if (vm.filteredRooms.some(x => x.some(y => y.Id == null) && !vm.filteredRooms.some(x => x.some(y => y.Id != null)))) {


                //Serach for combinations
                var foundCombination = false;
                var combinationSearchSize = vm.selectedSize[1];

                //Maxing it out, we need to search from the largest room, which should be the last room in the list
                if (combinationSearchSize == 10) {
                    combinationSearchSize = Math.ceil(vm.rooms.slice(-1)[0][0].SizeNumeric);
                }

                //We look for combinations starting from the largest size and searching downwards

                while (combinationSearchSize >= vm.selectedSize[0] && !foundCombination) {
                    var response = await fetch(`/umbraco/api/departmentsApi/GetRoomCombinations?departmentId=${vm.department.Id}&size=${combinationSearchSize}&take=2`)
                    if (response.status == 200) {
                        var data = await response.json();
                        foundCombination = true;
                        vm.combinations = data.Rooms;
                    } else {
                        combinationSearchSize--;
                        continue;
                    }
                }
                //No combinations was found, let's look at nearby departments
                if (!foundCombination) {
                    let nearbySearchSize = vm.selectedSize[0] >= 2 ? vm.selectedSize[0] - 2 : 0; //We start two below the lower boundary
                    let nearbyMaxSearchSize = vm.selectedSize[1] + 2;

                    //Maxing it out, we need to search to the largest room, which should be the last room in the list
                    if (nearbyMaxSearchSize == 10) {
                        nearbyMaxSearchSize = Math.ceil(vm.rooms.slice(-1)[0][0].SizeNumeric);
                    }

                    if (Number.isNaN(nearbyMaxSearchSize)) {
                        nearbyMaxSearchSize = 10; //Simply a failover if the above code fails to find a proper max. We do this to prevent and endless loop below
                    }

                    //We search all the nearby departments for available rooms
                    while (nearbySearchSize <= nearbyMaxSearchSize) {
                        for (let i = 0; i < vm.nearestDepartments.length; i++) {
                            if (!vm.availableDepartments.some(x => x.Id == vm.nearestDepartments[i].Id)) {
                                var response = await fetch(`/umbraco/api/departmentsApi/GetRoomsBySize?departmentId=${vm.nearestDepartments[i].Id}&size=${nearbySearchSize}&availability=true`);
                                if (response.status == 200) {
                                    vm.availableDepartments.push(vm.nearestDepartments[i]);
                                }
                            }
                        }

                        nearbySearchSize++;
                    }
                    //Sort the available departments based on the nearby departments
                    if (vm.availableDepartments.length > 0) {
                        var nearbyDepartmentsIds = vm.nearestDepartments.map(x => x.Id);
                        vm.availableDepartments.sort((a, b) => {
                            return nearbyDepartmentsIds.indexOf(a.Id) - nearbyDepartmentsIds.indexOf(b.Id);
                        });
                    }
                }

                if (!vm.initialFetch) {
                    vm.$toast.add({
                        severity: 'error', closable: false, summary: vm.dictionary.BookingFlowRoomsNoAvailableNotificationTitle, detail: vm.dictionary.BookingFlowRoomsNoAvailableNotificationText, life: 10000
                    });
                }

                vm.showAll = vm.roomListOptions[0];
                vm.disableAvailableButton = true;
                vm.showNoAvailableMessage = true;
            }
            else {
                vm.showAll = vm.roomListOptions[1];
                vm.showNoAvailableMessage = false;
                vm.disableAvailableButton = false;
                vm.getRooms(size[0], size[1], true);
            }
            vm.initialFetch = false;
            vm.page = 1;
            vm.$emit('toggleSpinner', false);

            await vm.$nextTick();


        },
        handleAddRoom() {
            const vm = this;

            vm.$emit("toggleSpinner", true);
            fetch(`/umbraco/api/departmentsapi/GetRoomByNumber?number=${vm.manualRoomNumber}&departmentId=${vm.department.Id}`)
                .then((response) => {
                    if (response.ok) {
                        return response;
                    }
                    throw new Error(response.statusText);
                })
                .then((response) => response.json())
                .then((data) => {
                    vm.$emit("toggleSpinner", false);
                    this.$emit("addRoom", [data]);
                })
                .catch((error) => {
                    vm.$emit("toggleSpinner", false);
                    vm.$toast.add({
                        severity: 'error', closable: false, summary: vm.dictionary.BookingFlowRoomsUnknownRoomErrorTitle, detail: vm.dictionary.BookingFlowRoomsUnknownRoomErrorText, life: 10000
                    });
                    console.log(error);
                });

        },
        goToDepartmentsOverview() {
            const vm = this;
            window.location.href = `${vm.parentLink}?lat=${vm.department.Coordinates.Latitude}&lng=${vm.department.Coordinates.Longitude}&city=${vm.department.City}`
        }
    },
    watch: {
        manualRoomNumber(newValue, oldValue) {
            const vm = this;
            if (newValue == null) {
                vm.manualRoomNumberValid = true;
            } else {
                const regex = new RegExp('\\d{1,5}');
                const regexResult = regex.test(newValue);
                vm.manualRoomNumberValid = regexResult && newValue < 100000;

            }
        },
        showAll(newValue, oldValue) {
            const vm = this;
            if (!newValue.value) {
                vm.getRooms(vm.selectedSize[0], vm.selectedSize[1], true);
            } else {
                vm.getRooms(vm.selectedSize[0], vm.selectedSize[1]);
            }
            vm.page = 1;
        },
        async showNoAvailableMessage(newValue, oldValue) {
            if (newValue == true) {

                window.dataLayer.push({
                    'event': bfdl.events.hasSeenNoAvailableRoomsMessage,
                    'value': true,
                });

                // this.$emit("hasSeenNoAvailableRoomsMessage");

                this.nearbyDepartmentsSlider = new Swiper(this.$refs.nearbyDepartmentsSlider, {
                    speed: 500,
                    loop: false,
                    slidesPerView: 1,
                    watchOverflow: true,
                    breakpoints: {
                        // when window width is >= 625px
                        625: {
                            slidesPerView: 2,
                        },
                        // when window width is >= 1125px
                        1125: {
                            slidesPerView: 3,
                        }
                    },
                    navigation: {
                        nextEl: '#nearbyDepartmentsSlider .booking__available-departments-next',
                        prevEl: '#nearbyDepartmentsSlider .booking__available-departments-prev'
                    },
                    spaceBetween: 15
                });

            } else {
                if (this.nearbyDepartmentsSlider) {
                    this.nearbyDepartmentsSlider.destroy();
                }
            }
        }
    }
};
</script>
